<template>
  <div class="formulaire-formation-gcr">
    <div v-if="loading" class="text-muted">
      <b-spinner small /> Chargement des formations...
    </div>

    <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

    <template v-else>
      <!-- MENU : campagne > groupe > affaire -->
      <div v-if="view === 'menu'">
        <h4>Formulaires de validation des formations</h4>
        <div v-if="!campaignList.length" class="text-muted">
          Aucune formation en attente de validation.
        </div>
        <div v-for="camp in campaignList" :key="camp.key" class="mb-4">
          <h5>{{ camp.label }}</h5>
          <div v-for="grp in camp.groups" :key="grp.key" class="mb-3">
            <h6 class="text-muted">{{ grp.label }}</h6>
            <ul class="list-group">
              <li
                v-for="aff in grp.affaires"
                :key="aff.key"
                class="list-group-item d-flex justify-content-between align-items-center"
                style="cursor:pointer"
                @click="openAffaire(camp.key, grp.key, aff.key)"
              >
                <span>{{ aff.label }} <small class="text-muted">({{ aff.num_affaire }})</small></span>
                <span>
                  <span class="badge badge-success badge-pill mr-1">{{ aff.validated }}/{{ aff.count }} validées</span>
                  <span v-if="aff.count > aff.validated" class="badge badge-warning badge-pill">{{ aff.count - aff.validated }} restante{{ (aff.count - aff.validated) > 1 ? 's' : '' }}</span>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- DÉTAIL AFFAIRE -->
      <div v-else-if="view === 'affaire' && currentAffaire">
        <button type="button" class="btn btn-link px-0 mb-2" @click="backToMenu">&larr; Retour au menu</button>
        <h4>{{ currentAffaire.label }} <small class="text-muted">({{ currentAffaire.num_affaire }})</small></h4>

        <div v-if="submitError" class="alert alert-danger">{{ submitError }}</div>

        <div class="row">
          <div class="col-md-3">
            <div class="list-group">
              <template v-for="p in currentAffaire.people">
                <button
                  :key="'person-' + p.identifiant_salarie_2"
                  type="button"
                  class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                  :class="{ active: p.identifiant_salarie_2 === selectedPersonId }"
                  @click="selectPerson(p.identifiant_salarie_2)"
                >
                  <span>{{ p.nom }} {{ p.prenom }}</span>
                  <span class="d-flex align-items-center">
                    <small class="text-muted mr-1">{{ validatedFormationsCount(p) }}/{{ p.formationIds.length }}</small>
                    <i v-if="p.complete" class="fas fa-check-circle text-success"></i>
                  </span>
                </button>
                <div
                  v-if="p.identifiant_salarie_2 === selectedPersonId"
                  :key="'formations-' + p.identifiant_salarie_2"
                  class="list-group-item p-0 bg-light"
                >
                  <button
                    v-for="fid in getSortedFormationIds(p)"
                    :key="fid"
                    type="button"
                    class="list-group-item list-group-item-action pl-4 py-1 border-0 d-flex justify-content-between align-items-center"
                    :class="{ 'font-weight-bold': fid === currentFormationId }"
                    @click="selectFormation(fid)"
                  >
                    <small>{{ formationsMap[fid].titre }}</small>
                    <i v-if="formationComplete(formationsMap[fid])" class="fas fa-check-circle text-success ml-1"></i>
                    <i v-else class="far fa-circle text-muted ml-1"></i>
                  </button>
                </div>
              </template>
            </div>
          </div>

          <div class="col-md-9" v-if="selectedPerson" ref="personSection">
            <form @submit.prevent novalidate>
              <div class="form-row">
                <div class="form-group col-md-4">
                  <label>Nom</label>
                  <input class="form-control" :value="selectedPerson.nom" disabled />
                </div>
                <div class="form-group col-md-4">
                  <label>Prénom</label>
                  <input class="form-control" :value="selectedPerson.prenom" disabled />
                </div>
                <div class="form-group col-md-4">
                  <label>Date de naissance *</label>
                  <input
                    type="date"
                    class="form-control"
                    v-model="selectedPerson.naissance"
                    @keydown.enter.prevent
                  />
                </div>
              </div>
              <div class="form-row">
                <div class="form-group col-md-4">
                  <label>Date d'entrée *</label>
                  <input
                    type="date"
                    class="form-control"
                    v-model="selectedPerson.entree"
                    @keydown.enter.prevent
                  />
                </div>
                <div class="form-group col-md-4">
                  <label>CSP *</label>
                  <select class="form-control" v-model="selectedPerson.csp" @keydown.enter.prevent>
                    <option value="">À choisir</option>
                    <option v-for="o in CSP_OPTIONS" :key="o" :value="o">{{ o }}</option>
                  </select>
                </div>
                <div class="form-group col-md-4">
                  <label>Genre *</label>
                  <select class="form-control" v-model="selectedPerson.sexe" @keydown.enter.prevent>
                    <option value="">À choisir</option>
                    <option v-for="o in SEXE_OPTIONS" :key="o" :value="o">{{ o }}</option>
                  </select>
                </div>
              </div>
              <div class="form-row">
                <div class="form-group col-md-4">
                  <label>Type de contrat *</label>
                  <select class="form-control" v-model="selectedPerson.contrat" @keydown.enter.prevent>
                    <option value="">À choisir</option>
                    <option v-for="o in CONTRAT_OPTIONS" :key="o" :value="o">{{ o }}</option>
                  </select>
                </div>
                <div class="form-group col-md-4">
                  <label>Échelon</label>
                  <select class="form-control" v-model="selectedPerson.echelon" @keydown.enter.prevent>
                    <option value=""></option>
                    <option v-for="o in ECHELON_OPTIONS" :key="o" :value="o">{{ o }}</option>
                  </select>
                </div>
              </div>
              <div class="form-group">
                <label>Commentaire salarié</label>
                <textarea class="form-control" rows="3" v-model="selectedPerson.commentaire" @keydown.enter.prevent></textarea>
              </div>

              <div v-if="selectedPerson.warnings.length" class="alert alert-warning py-2">
                <div v-for="(w, i) in selectedPerson.warnings" :key="i"><small>{{ w }}</small></div>
              </div>
            </form>

            <h6 class="mt-4 d-flex justify-content-between align-items-center">
              <span>Formation <small class="text-muted" v-if="currentPersonFormationIds.length">({{ currentFormationIndex + 1 }}/{{ currentPersonFormationIds.length }})</small></span>
              <small v-if="currentFormation" :class="formationComplete(currentFormation) ? 'text-success' : 'text-muted'">
                <i :class="formationComplete(currentFormation) ? 'fas fa-check-circle' : 'far fa-circle'" class="mr-1"></i>{{ formationComplete(currentFormation) ? 'Validée' : 'Non validée' }}
              </small>
            </h6>
            <div class="card mb-2" v-if="currentFormation" ref="formationSection">
              <div class="card-header">{{ currentFormation.titre }}</div>
              <div class="card-body">
                <div class="form-row">
                  <div class="col-md-3"><small class="text-muted">Début</small><div>{{ currentFormation.debut }}</div></div>
                  <div class="col-md-3"><small class="text-muted">Fin</small><div>{{ currentFormation.fin }}</div></div>
                  <div class="col-md-3"><small class="text-muted">Durée</small><div>{{ currentFormation.duree }}</div></div>
                  <div class="col-md-3"><small class="text-muted">Montant</small><div>{{ currentFormation.montant }}</div></div>
                </div>
                <div class="form-row mt-2">
                  <div class="col-md-3"><small class="text-muted">Ville</small><div>{{ currentFormation.ville }} {{ currentFormation.code_postal }}</div></div>
                  <div class="form-group col-md-3">
                    <label class="mb-0"><small>Taux horaire *</small></label>
                    <input type="number" class="form-control form-control-sm" v-model="currentFormation.taux_horaire" @keydown.enter.prevent />
                  </div>
                  <div class="col-md-6" v-if="currentFormation.id_doc">
                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="viewDocument(currentFormation.id_doc)">
                      <i class="fas fa-file-pdf mr-1"></i>{{ openDocId === currentFormation.id_doc ? 'Masquer le document' : 'Voir le document' }}
                    </button>
                  </div>
                </div>
                <div v-if="openDocId === currentFormation.id_doc && pdfUrls[currentFormation.id_doc]" class="ged-inline-viewer mt-2 border rounded">
                  <div class="d-flex align-items-center p-2 border-bottom bg-light" style="gap:8px">
                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="openDocId = null">&times; Replier</button>
                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="openPdfNewTab(currentFormation.id_doc)">&#x29c9; Ouvrir dans un nouvel onglet</button>
                    <button type="button" class="btn btn-sm btn-outline-primary" @click="downloadPdf(currentFormation.id_doc, currentFormation.titre)">&#8595; Télécharger</button>
                  </div>
                  <div class="pdf-pages p-2">
                    <vue-pdf-embed :source="pdfUrls[currentFormation.id_doc]" />
                  </div>
                </div>
                <div class="form-group mt-2 mb-0">
                  <div class="form-check">
                    <input type="checkbox" class="form-check-input" :id="'absent-' + currentFormation.id" v-model="currentFormation.absent" />
                    <label class="form-check-label" :for="'absent-' + currentFormation.id">Absent au stage</label>
                  </div>
                </div>
                <div class="form-group mb-0">
                  <label class="mb-0"><small>Commentaire formation</small></label>
                  <textarea class="form-control form-control-sm" rows="2" v-model="currentFormation.commentaire" @keydown.enter.prevent></textarea>
                </div>
              </div>
            </div>
            <div v-else class="text-muted mb-2">Aucune formation pour ce salarié.</div>

            <div class="d-flex justify-content-between">
              <button type="button" class="btn btn-outline-secondary" :disabled="!canGoPrev" @click="goPrevFormation">
                &larr; {{ prevButtonLabel }}
              </button>
              <button type="button" class="btn btn-outline-secondary" :disabled="!canGoNext" @click="goNextFormation">
                {{ nextButtonLabel }} &rarr;
              </button>
            </div>
          </div>
        </div>

        <div class="alert alert-info py-2 mt-3 mb-2">
          <small><i class="fas fa-info-circle mr-1"></i>Seuls les salariés dont toutes les formations sont validées (taux horaire renseigné + formation consultée) seront envoyés.</small>
        </div>
        <div class="mt-1">
          <button type="button" class="btn btn-secondary mr-2" @click="backToMenu">Retour au menu (sans valider)</button>
          <button type="button" class="btn btn-primary" :disabled="!hasCompletePeople || submitting" @click="submitAffaire" title="Seuls les salariés avec toutes leurs formations validées seront envoyés">
            <b-spinner v-if="submitting" small />
            Valider
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* Le fond violet/rose vient de l'environnement de test (fond de page), pas du
   composant : sans fond opaque ici, les éléments Bootstrap semi-transparents
   (ex: .card-header en rgba(0,0,0,.03)) laissent transparaître cette couleur.
   On force donc un fond blanc opaque sur tout le formulaire, ainsi que sur les
   cards/card-header explicitement (le thème de l'appli peut leur appliquer une
   teinte semi-transparente propre, indépendamment du fond du composant). */
.formulaire-formation-gcr {
  background: #fff;
}
.formulaire-formation-gcr .card,
.formulaire-formation-gcr .card-header {
  background-color: #fff;
}

/* Viewer PDF inline (repris du formulaire HTML d'origine, form_gcr.html) : rendu par
   vue-pdf-embed (canvas, pas de chrome/toolbar natif du navigateur), dans un conteneur
   scrollable assez grand pour lire confortablement sans "l'éditeur" (barre d'outils PDF
   native) autour. */
.formulaire-formation-gcr .ged-inline-viewer .pdf-pages {
  max-height: 80vh;
  overflow-y: auto;
  background: #f8f9fa;
}
</style>

<script>
// Pas d'import ES ici : ce composant est déployé/exécuté en pratique via component_code
// (contenu du script copié/collé tel quel dans l'éditeur d'élément dashboard, extrait par regex
// puis évalué via new Function() par dashboard/vueComponent.vue :: compileComponent() - qui
// n'accepte aucune syntaxe import/export). VuePdfEmbed n'est donc PAS importé ici : il est
// enregistré globalement (vrai import webpack) dans dashboard/vueComponent.vue et fourni à tout
// composant utilisateur (component_code ou component_file) via extendComponentOptions() ->
// components: { ..., VuePdfEmbed }. Le tag <vue-pdf-embed> ci-dessous s'y résout automatiquement.
// this.database / this.treatments / this.document / this.user / this.client sont
// injectés automatiquement par vueComponent.vue :: extendComponentOptions().
//
// NB : la validation d'un salarié/formation N'ÉCRIT PLUS directement en base depuis le front
// (les concessions n'ont que des droits de LECTURE sur /database) : elle passe entièrement par
// this.code.run_by_name('campagne_validation', ...) (cf gcrValidationScript.js, exécuté
// server-side, seul habilité à écrire). CE MÊME SCRIPT déclenche aussi directement le projet EDI
// (via le helper sandbox trigger_treatment(), cf vm2ProcessRunner.js) quand le constat "toutes
// les formations du groupe+campagne sont prêtes" est atomiquement vrai — le front n'a PLUS à
// faire cet appel lui-même (ni CSV, ni this.treatments.create : le déclenchement se fait avec de
// simples treatment_params JSON, pas de fichier). L'ancien mécanisme (CSV au format table
// `formulaire` + this.treatments.create sur le projet 4b0cf353-...) est abandonné (simplification
// demandée par l'utilisateur).

var REQUIRED_FIELDS = ['naissance', 'entree', 'csp', 'sexe', 'contrat'];

var CSP_OPTIONS = ['OUVRIER', 'EMPLOYE', 'ETAM', 'CADRE'];
var SEXE_OPTIONS = ['F', 'M'];
var CONTRAT_OPTIONS = ['CDI', 'CDD'];
var ECHELON_OPTIONS = (function() {
  var opts = [];
  [1,2,3,4,5,6,7,8,9,10,11,12,17,18,19,20,21,22,23,24,25].forEach(function(n) {
    opts.push('Echelon : ' + n);
  });
  [1,2,3,4].forEach(function(n) {
    ['A', 'B', 'C'].forEach(function(d) {
      opts.push('Niveau : ' + n + ', Degré : ' + d);
    });
  });
  opts.push('Niveau : 5, Degré : A');
  return opts;
})();

// user.details.alcor est une liste au format "'N11225','N43872',..." (cf
// gcrSearchScript.js::parseQuotedList()) : on l'analyse en tableau de codes alcor
// bruts plutôt que de la ré-injecter telle quelle dans une condition SQL.
function parseQuotedList(raw) {
  if (!raw) return [];
  var out = [];
  var re = /'([^']*)'/g;
  var m;
  while ((m = re.exec(raw)) !== null) {
    if (m[1]) out.push(m[1]);
  }
  return out;
}

function isSafeAlcor(v) {
  return /^[A-Za-z0-9_-]{1,20}$/.test(v);
}

function parseIsoDate(str) {
  if (!str) return null;
  var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function parseFrDate(str) {
  if (!str) return null;
  var m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(str);
  if (!m) return null;
  return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
}

function ageFromNaissance(str) {
  var d = parseIsoDate(str);
  if (!d) return null;
  var now = new Date();
  var age = now.getFullYear() - d.getFullYear();
  var m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

export default {
  props: ['format', 'data', 'client'],
  data() {
    return {
      loading: true,
      error: null,
      submitting: false,
      submitError: null,

      personsMap: {},
      formationsMap: {},

      view: 'menu',
      currentCampKey: null,
      currentGroupKey: null,
      currentAffaireKey: null,
      selectedPersonId: null,

      openDocId: null,
      pdfUrls: {},
      currentFormationId: null,
      viewedFormations: {},

      CSP_OPTIONS: CSP_OPTIONS,
      SEXE_OPTIONS: SEXE_OPTIONS,
      CONTRAT_OPTIONS: CONTRAT_OPTIONS,
      ECHELON_OPTIONS: ECHELON_OPTIONS
    };
  },

  computed: {
    campaignActuelle() {
      var v = this.client && this.client.var ? this.client.var : {};
      return v.campagne_nom ? String(v.campagne_nom).trim() : null;
    },

    campaignList() {
      var self = this;
      var campActuelle = this.campaignActuelle;
      var camps = {};

      Object.keys(this.personsMap).forEach(function(pid) {
        var p = self.personsMap[pid];
        var isActuelle = !!campActuelle && p.campagne === campActuelle;
        var campKey = isActuelle ? 'actuelle' : ('rattrapage:' + (p.campagne || '(sans campagne)'));
        var campLabel = isActuelle
          ? ('Campagne actuelle' + (campActuelle ? (' - ' + campActuelle) : ''))
          : ('Rattrapage - ' + (p.campagne || '(sans campagne)'));

        if (!camps[campKey]) camps[campKey] = { key: campKey, label: campLabel, groups: {} };
        var groups = camps[campKey].groups;
        var groupKey = p.groupe_nom;
        if (!groups[groupKey]) groups[groupKey] = { key: groupKey, label: groupKey, affaires: {} };
        var affaires = groups[groupKey].affaires;
        var affKey = p.num_affaire;
        if (!affaires[affKey]) {
          affaires[affKey] = {
            key: affKey,
            label: p.affaire_nom || p.num_affaire,
            num_affaire: p.num_affaire,
            alcor: p.alcor,
            campagne: p.campagne,
            count: 0,
            validated: 0,
            peopleIds: []
          };
        }
        affaires[affKey].count += p.formationIds.length;
        affaires[affKey].validated += self.validatedFormationsCount(p);
        affaires[affKey].peopleIds.push(pid);
      });

      return Object.keys(camps).map(function(ck) {
        var camp = camps[ck];
        return {
          key: camp.key,
          label: camp.label,
          groups: Object.keys(camp.groups).map(function(gk) {
            var grp = camp.groups[gk];
            return { key: grp.key, label: grp.label, affaires: Object.keys(grp.affaires).map(function(ak) { return grp.affaires[ak]; }) };
          })
        };
      }).sort(function(a, b) {
        // La campagne actuelle (key === 'actuelle') apparaît toujours en premier, les
        // rattrapages ensuite (ordre alphabétique du libellé).
        if (a.key === 'actuelle') return -1;
        if (b.key === 'actuelle') return 1;
        return a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' });
      });
    },

    currentAffaire() {
      if (!this.currentCampKey) return null;
      var camp = this.campaignList.find(function(c) { return c.key === this.currentCampKey; }, this);
      if (!camp) return null;
      var grp = camp.groups.find(function(g) { return g.key === this.currentGroupKey; }, this);
      if (!grp) return null;
      var aff = grp.affaires.find(function(a) { return a.key === this.currentAffaireKey; }, this);
      if (!aff) return null;

      var self = this;
      var people = aff.peopleIds
        .map(function(id) { return self.personsMap[id]; })
        .filter(Boolean)
        .map(function(p) {
          return Object.assign({}, p, {
            complete: self.isPersonComplete(p),
            warnings: self.personWarnings(p)
          });
        });
      people.sort(function(a, b) {
        return (a.nom + a.prenom).localeCompare(b.nom + b.prenom, 'fr', { sensitivity: 'base' });
      });

      return Object.assign({}, aff, { people: people });
    },

    selectedPerson() {
      if (!this.currentAffaire) return null;
      return this.currentAffaire.people.find(function(p) { return p.identifiant_salarie_2 === this.selectedPersonId; }, this) || null;
    },

    hasCompletePeople() {
      return !!(this.currentAffaire && this.currentAffaire.people.some(function(p) { return p.complete; }));
    },

    currentPersonIndex() {
      if (!this.currentAffaire || !this.selectedPersonId) return -1;
      return this.currentAffaire.people.findIndex(function(p) { return p.identifiant_salarie_2 === this.selectedPersonId; }, this);
    },

    currentPersonFormationIds() {
      return this.selectedPerson ? this.getSortedFormationIds(this.selectedPerson) : [];
    },

    currentFormationIndex() {
      return this.currentPersonFormationIds.indexOf(this.currentFormationId);
    },

    currentFormation() {
      return this.currentFormationId ? this.formationsMap[this.currentFormationId] : null;
    },

    isFirstFormationOfPerson() {
      return this.currentFormationIndex <= 0;
    },

    isLastFormationOfPerson() {
      return this.currentFormationIndex === this.currentPersonFormationIds.length - 1;
    },

    canGoPrev() {
      if (!this.currentAffaire) return false;
      if (!this.isFirstFormationOfPerson) return true;
      return this.currentPersonIndex > 0;
    },

    canGoNext() {
      if (!this.currentAffaire) return false;
      if (!this.isLastFormationOfPerson) return true;
      return this.currentPersonIndex < this.currentAffaire.people.length - 1;
    },

    prevButtonLabel() {
      return this.isFirstFormationOfPerson ? 'Salarié précédent' : 'Formation précédente';
    },

    nextButtonLabel() {
      return this.isLastFormationOfPerson ? 'Salarié suivant' : 'Formation suivante';
    }
  },

  created() {
    this.loadData();
  },

  methods: {
    isPersonComplete(p) {
      var self = this;
      var identityOk = REQUIRED_FIELDS.every(function(f) {
        return p[f] !== null && p[f] !== undefined && String(p[f]).trim() !== '';
      });
      if (!identityOk) return false;
      // Un salarié n'est complet que si TOUTES ses formations sont elles-mêmes validées
      // (taux horaire renseigné + formation consultée) : seuls les salariés complets sont
      // envoyés (cf submitAffaire -> completePeople).
      return (p.formationIds || []).every(function(fid) {
        return self.formationComplete(self.formationsMap[fid]);
      });
    },

    formationComplete(f) {
      if (!f) return false;
      // Une formation est validée si le taux horaire est renseigné ET si la formation a été
      // vue par l'utilisateur (affichée au moins une fois - cf markFormationViewed(), appelé à
      // chaque navigation vers une formation).
      var tauxOk = f.taux_horaire !== null && f.taux_horaire !== undefined && String(f.taux_horaire).trim() !== '';
      var viewed = !!this.viewedFormations[f.id];
      return tauxOk && viewed;
    },

    validatedFormationsCount(p) {
      var self = this;
      return (p.formationIds || []).filter(function(fid) { return self.formationComplete(self.formationsMap[fid]); }).length;
    },

    markFormationViewed(fid) {
      if (fid) this.$set(this.viewedFormations, fid, true);
    },

    getSortedFormationIds(person) {
      var self = this;
      var ids = (person && person.formationIds ? person.formationIds.slice() : []);
      ids.sort(function(a, b) {
        var da = parseFrDate(self.formationsMap[a] && self.formationsMap[a].debut);
        var db = parseFrDate(self.formationsMap[b] && self.formationsMap[b].debut);
        if (da && db) return da - db;
        if (da) return -1;
        if (db) return 1;
        return 0;
      });
      return ids;
    },

    personWarnings(p) {
      var self = this;
      var warnings = [];
      if (p.naissance) {
        var age = ageFromNaissance(p.naissance);
        if (age !== null && (age < 15 || age > 75)) {
          warnings.push('Âge (' + age + ' ans) inhabituel pour la date de naissance renseignée.');
        }
      }
      if (p.entree) {
        var entreeDate = parseIsoDate(p.entree);
        if (entreeDate) {
          (p.formationIds || []).forEach(function(fid) {
            var f = self.formationsMap[fid];
            if (f && f.debut) {
              var d = parseFrDate(f.debut);
              if (d && entreeDate > d) {
                warnings.push("Date d'entrée postérieure à la date de début de la formation « " + f.titre + " ».");
              }
            }
          });
        }
      }
      return warnings;
    },

    async loadData() {
      this.loading = true;
      this.error = null;
      try {
        // Un admin smartmydata (pas une concession) voit toutes les formations, sans filtre
        // alcor - même convention que store.getters.isUserAdmin (rules === null + présence de
        // details.client_uid dans le JWT).
        var isAdmin = !!(this.user && this.user.rules === null && this.user.details && this.user.details.client_uid !== undefined);

        // Formations "en attente de validation" : uniquement statut_form CRÉER ou ENVOYÉ
        // (valeur explicitement demandée par l'utilisateur) - tout autre statut (COMPLÉTÉ,
        // FERMER, SANS CONTACT, etc.) est exclu.
        var fsConditions = "`statut_form` IN ('CRÉER', 'ENVOYÉ')";

        if (!isAdmin) {
          var alcors = parseQuotedList(this.user && this.user.details && this.user.details.alcor);
          var safeAlcors = alcors.filter(isSafeAlcor);

          if (!safeAlcors.length) {
            this.error = 'Aucun alcor associé à votre compte.';
            this.loading = false;
            return;
          }

          var alcorList = safeAlcors.map(function(a) { return "'" + a + "'"; }).join(',');
          fsConditions += " AND `alcor` IN (" + alcorList + ")";
        }

        var requestObj = {
          tables: [
            {
              name: 'fs', table: 'formation_suivie', conditions: fsConditions,
              columns: [
                { column: 'id', name: 'id', select: true },
                { column: 'identifiant_salarie_2', name: 'identifiant_salarie_2', select: true },
                { column: 'num_affaire', name: 'num_affaire', select: true },
                { column: 'alcor', name: 'alcor', select: true },
                { column: 'campagne', name: 'campagne', select: true },
                { column: 'titre', name: 'titre', select: true },
                { column: 'debut', name: 'debut', select: true },
                { column: 'fin', name: 'fin', select: true },
                { column: 'duree', name: 'duree', select: true },
                { column: 'montant', name: 'montant', select: true },
                { column: 'code_postal', name: 'code_postal', select: true },
                { column: 'ville', name: 'ville', select: true },
                { column: 'action_reference', name: 'action_reference', select: true },
                { column: 'num_ligne_fichier', name: 'num_ligne_fichier', select: true },
                { column: 'id_doc', name: 'id_doc', select: true }
              ]
            },
            {
              name: 'sr', table: 'salarie_renault', join: 'INNER JOIN',
              join_rules: '`sr`.`identifiant_salarie_2` = `fs`.`identifiant_salarie_2`',
              columns: [
                { column: 'nom', name: 'nom', select: true },
                { column: 'prenom', name: 'prenom', select: true },
                { column: 'sexe', name: 'sexe', select: true },
                { column: 'naissance', name: 'naissance', select: true },
                { column: 'entree', name: 'entree', select: true },
                { column: 'csp', name: 'csp', select: true },
                { column: 'contrat', name: 'contrat', select: true },
                { column: 'echelon', name: 'echelon', select: true },
                { column: 'commentaire', name: 'commentaire_salarie', select: true }
              ]
            },
            {
              name: 'aff', table: 'affaires', join: 'INNER JOIN',
              join_rules: '`aff`.`num_affaire` = `fs`.`num_affaire`',
              columns: [
                { column: 'affaire', name: 'affaire_nom', select: true },
                { column: 'group', name: 'groupe_nom', select: true },
                { column: 'siret', name: 'siret', select: true }
              ]
            }
          ],
          order_by: '`fs`.`campagne`, `aff`.`group`, `aff`.`affaire`, `sr`.`nom`, `sr`.`prenom`'
        };

        var resp = await this.database.request(requestObj, {});
        if (!resp || resp.error) {
          console.error(resp && resp.error);
          this.error = 'Erreur lors du chargement des formations.';
          this.loading = false;
          return;
        }

        this.buildMaps(resp.data || []);
      } catch (err) {
        console.error(err);
        this.error = 'Erreur lors du chargement des formations.';
      }
      this.loading = false;
    },

    buildMaps(rawRows) {
      var persons = {};
      var formations = {};

      rawRows.forEach(function(r) {
        if (!persons[r.identifiant_salarie_2]) {
          persons[r.identifiant_salarie_2] = {
            identifiant_salarie_2: r.identifiant_salarie_2,
            nom: r.nom,
            prenom: r.prenom,
            sexe: r.sexe || '',
            naissance: r.naissance || '',
            entree: r.entree || '',
            csp: r.csp || '',
            contrat: r.contrat || '',
            echelon: r.echelon || '',
            commentaire: r.commentaire_salarie || '',
            num_affaire: r.num_affaire,
            alcor: r.alcor,
            campagne: (r.campagne || '').trim(),
            groupe_nom: r.groupe_nom || '(sans groupe)',
            affaire_nom: r.affaire_nom || r.num_affaire,
            siret: r.siret,
            formationIds: []
          };
        }
        persons[r.identifiant_salarie_2].formationIds.push(r.id);
        formations[r.id] = {
          id: r.id,
          identifiant_salarie_2: r.identifiant_salarie_2,
          titre: r.titre,
          debut: r.debut,
          fin: r.fin,
          duree: r.duree,
          montant: r.montant,
          code_postal: r.code_postal,
          ville: r.ville,
          action_reference: r.action_reference,
          num_ligne_fichier: r.num_ligne_fichier,
          id_doc: r.id_doc,
          siret: r.siret,
          taux_horaire: 12,
          commentaire: '',
          absent: false
        };
      });

      this.personsMap = persons;
      this.formationsMap = formations;
    },

    openAffaire(campKey, groupKey, affKey) {
      this.currentCampKey = campKey;
      this.currentGroupKey = groupKey;
      this.currentAffaireKey = affKey;
      this.view = 'affaire';
      var affaire = this.currentAffaire;
      if (affaire && affaire.people.length) {
        // On se positionne toujours sur le premier salarié de la liste (ordre alphabétique).
        var first = affaire.people[0];
        this.selectedPersonId = first.identifiant_salarie_2;
        var ids = this.getSortedFormationIds(first);
        this.currentFormationId = ids.length ? ids[0] : null;
        this.markFormationViewed(this.currentFormationId);
      } else {
        this.selectedPersonId = null;
        this.currentFormationId = null;
      }
    },

    selectPerson(pid) {
      if (pid === this.selectedPersonId) return;
      this.selectedPersonId = pid;
      var person = this.currentAffaire && this.currentAffaire.people.find(function(p) { return p.identifiant_salarie_2 === pid; });
      var ids = person ? this.getSortedFormationIds(person) : [];
      this.currentFormationId = ids.length ? ids[0] : null;
      this.markFormationViewed(this.currentFormationId);
      this.scrollToPersonTop();
    },

    selectFormation(fid) {
      this.currentFormationId = fid;
      this.markFormationViewed(fid);
      this.scrollToFormationTop();
    },

    goNextFormation() {
      var ids = this.currentPersonFormationIds;
      var idx = ids.indexOf(this.currentFormationId);
      if (idx > -1 && idx < ids.length - 1) {
        this.currentFormationId = ids[idx + 1];
        this.markFormationViewed(this.currentFormationId);
        this.scrollToFormationTop();
      } else {
        this.goToAdjacentPerson(1, false);
      }
    },

    goPrevFormation() {
      var ids = this.currentPersonFormationIds;
      var idx = ids.indexOf(this.currentFormationId);
      if (idx > 0) {
        this.currentFormationId = ids[idx - 1];
        this.markFormationViewed(this.currentFormationId);
        this.scrollToFormationTop();
      } else {
        this.goToAdjacentPerson(-1, true);
      }
    },

    goToAdjacentPerson(direction, landOnLast) {
      var people = this.currentAffaire && this.currentAffaire.people;
      if (!people || !people.length) return;
      var idx = people.findIndex(function(p) { return p.identifiant_salarie_2 === this.selectedPersonId; }, this);
      var nextIdx = idx + direction;
      if (nextIdx < 0 || nextIdx >= people.length) return;
      var person = people[nextIdx];
      this.selectedPersonId = person.identifiant_salarie_2;
      var ids = this.getSortedFormationIds(person);
      this.currentFormationId = ids.length ? (landOnLast ? ids[ids.length - 1] : ids[0]) : null;
      this.markFormationViewed(this.currentFormationId);
      this.scrollToPersonTop();
    },

    scrollToPersonTop() {
      var self = this;
      this.$nextTick(function() {
        if (self.$refs.personSection && self.$refs.personSection.scrollIntoView) {
          self.$refs.personSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },

    scrollToFormationTop() {
      var self = this;
      this.$nextTick(function() {
        if (self.$refs.formationSection && self.$refs.formationSection.scrollIntoView) {
          self.$refs.formationSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },

    backToMenu() {
      this.view = 'menu';
      this.currentCampKey = null;
      this.currentGroupKey = null;
      this.currentAffaireKey = null;
      this.selectedPersonId = null;
      this.currentFormationId = null;
      this.submitError = null;
    },

    async viewDocument(idDoc) {
      if (!idDoc) return;
      if (this.openDocId === idDoc) {
        this.openDocId = null;
        return;
      }
      this.openDocId = idDoc;
      if (this.pdfUrls[idDoc]) return;
      try {
        var info = await this.document.getInfoFile(idDoc, true).catch(function() { return null; });
        var libelle = (info && info.libelle) || 'document.pdf';
        var blob = await this.document.getFile(idDoc, libelle, false, 'blob', true);
        // documentCtrl.js::get() force le Content-Type à application/octet-stream côté back
        // pour TOUS les documents (quel que soit le type réel) : le Blob renvoyé par axios a
        // donc lui aussi ce type générique. Un <iframe src="blob:..."> sur un blob
        // octet-stream déclenche un TÉLÉCHARGEMENT immédiat (nom = uuid opaque du blob:, d'où
        // le "nom de fichier = uid" constaté) au lieu d'afficher le PDF. On force donc
        // explicitement le type MIME du blob à application/pdf avant de créer l'URL, pour que
        // le navigateur l'affiche en ligne dans l'iframe.
        var pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
        this.$set(this.pdfUrls, idDoc, window.URL.createObjectURL(pdfBlob));
      } catch (err) {
        console.error(err);
        this.openDocId = null;
      }
    },

    openPdfNewTab(idDoc) {
      var url = this.pdfUrls[idDoc];
      if (url) window.open(url, '_blank', 'width=900,height=700,resizable=yes,scrollbars=yes');
    },

    downloadPdf(idDoc, libelle) {
      var url = this.pdfUrls[idDoc];
      if (!url) return;
      var a = document.createElement('a');
      a.href = url;
      a.download = (libelle || 'document') + '.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },

    buildPersonPayload(p) {
      var self = this;
      return {
        identifiant_salarie_2: p.identifiant_salarie_2,
        naissance: p.naissance || null,
        entree: p.entree || null,
        csp: p.csp || null,
        sexe: p.sexe || null,
        contrat: p.contrat || null,
        echelon: p.echelon || null,
        commentaire: p.commentaire || null,
        formations: (p.formationIds || []).map(function(fid) {
          var f = self.formationsMap[fid] || {};
          return {
            id: f.id,
            commentaire: f.commentaire || null,
            absent: !!f.absent
          };
        })
      };
    },

    // Déclenchement du projet EDI (64d903f8-...) : fait ENTIÈREMENT côté back par
    // gcrValidationScript.js (via le helper sandbox trigger_treatment(), cf
    // vm2ProcessRunner.js) une fois le constat atomique fait. Rien à faire ici côté front
    // au-delà d'afficher le résultat (result.edi_triggered / result.edi_error, cf submitAffaire).

    async submitAffaire() {
      var affaire = this.currentAffaire;
      if (!affaire) return;
      var completePeople = affaire.people.filter(function(p) { return p.complete; });
      if (!completePeople.length) return;

      this.submitting = true;
      this.submitError = null;

      try {
        var self = this;
        var payload = {
          campagne_actuelle: this.campaignActuelle,
          people: completePeople.map(function(p) { return self.buildPersonPayload(p); })
        };

        var resp = await this.code.run_by_name('campagne_validation', payload);
        if (resp.errors) {
          throw new Error(typeof resp.errors === 'string' ? resp.errors : "Erreur lors de la validation.");
        }

        var result = resp.result || {};
        if (result.error) throw new Error(result.error);

        var updatedIds = result.updated || [];
        var rejected = result.rejected || [];

        // On ne retire localement que les formations réellement mises à jour côté back
        // (jamais celles rejetées : conflit d'édition concurrente ou campagne close entre
        // l'ouverture de la page et l'envoi).
        completePeople.forEach(function(p) {
          var remainingIds = (p.formationIds || []).filter(function(fid) {
            if (updatedIds.indexOf(fid) === -1) return true;
            self.$delete(self.formationsMap, fid);
            return false;
          });
          if (!remainingIds.length) {
            self.$delete(self.personsMap, p.identifiant_salarie_2);
          } else if (self.personsMap[p.identifiant_salarie_2]) {
            self.personsMap[p.identifiant_salarie_2].formationIds = remainingIds;
          }
        });

        if (rejected.length) {
          this.submitError = "Certaines lignes n'ont pas pu être validées (déjà traitées entre-temps ou campagne close) : " +
            rejected.length + " ligne(s) concernée(s). Les autres salariés ont bien été validés.";
        }

        if (result.edi_error) {
          console.error('Erreur lors du déclenchement du projet EDI (côté back):', result.edi_error);
        }

        var camp = this.campaignList.find(function(c) { return c.key === this.currentCampKey; }, this);
        var grp = camp && camp.groups.find(function(g) { return g.key === this.currentGroupKey; }, this);
        var aff = grp && grp.affaires.find(function(a) { return a.key === this.currentAffaireKey; }, this);

        if (!aff || !aff.peopleIds.length) {
          this.backToMenu();
        } else {
          var next = this.currentAffaire.people[0];
          this.selectedPersonId = next ? next.identifiant_salarie_2 : null;
          var nextIds = next ? this.getSortedFormationIds(next) : [];
          this.currentFormationId = nextIds.length ? nextIds[0] : null;
          this.markFormationViewed(this.currentFormationId);
        }
      } catch (err) {
        console.error(err);
        this.submitError = "Erreur lors de l'envoi du formulaire : " + (err.message || err);
      }


      this.submitting = false;
    }
  }
};
</script>
